import { ScrollReveal, SplitReveal, MediaFrame, TiltCard } from '@/components/motion';
import type { CurriculumGroup } from '@/data/programmes';
import { cn } from '@ibas/ui';

export interface ProgrammeCurriculumProps {
  /** Verified curriculum groups (module lists / study structures / routes). */
  groups: readonly CurriculumGroup[];
  /** Optional lead banner image (a real media-library asset, e.g. a module banner). */
  lead?: { image: string; alt: string };
  /**
   * The mandated specialisation caveat, rendered as a footnote when any group
   * lists specialisations or routes. Omit when no group carries them.
   */
  caveat?: string;
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * ProgrammeCurriculum — the data-driven "What you'll study" section.
 *
 * Renders programme.curriculum as a SplitReveal heading, an optional lead banner
 * (MediaFrame), and a ScrollReveal-staggered grid of TiltCard group cards, each
 * listing its modules with gold bullet markers. A group may be note-only or hold
 * a single item (e.g. a diploma's optional specialisation) and still renders
 * cleanly. When the groups include specialisations/routes, the required
 * SPECIALISATION_CAVEAT is shown as a footnote.
 *
 * Reduced motion / touch: SplitReveal resolves to its static end-state, the
 * ScrollReveal reveals to visible, and TiltCard renders flat — the whole section
 * is a complete, readable composition with no motion. Nothing is gated on motion.
 *
 * Self-hides when there are no groups, so a programme without a verified module
 * list (the on-site masters) never renders an empty shell.
 */
export function ProgrammeCurriculum({
  groups,
  lead,
  caveat,
  className,
  id,
}: ProgrammeCurriculumProps) {
  if (groups.length === 0) return null;

  return (
    <section id={id} className={cn('bg-muted/40 py-16 md:py-24', className)}>
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="mb-10 max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Curriculum
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            What you’ll study.
          </SplitReveal>
        </header>

        {lead && (
          <ScrollReveal from="up" className="mb-10">
            <MediaFrame
              image={lead.image}
              alt={lead.alt}
              aspect="aspect-[16/6]"
              rounded="rounded-2xl"
              position="50% 50%"
            />
          </ScrollReveal>
        )}

        <ScrollReveal
          from="up"
          stagger={0.08}
          className="grid grid-cols-1 gap-6 sm:grid-cols-2"
        >
          {groups.map((group) => (
            <TiltCard key={group.group} max={5} className="h-full">
              <div className="flex h-full flex-col gap-4 rounded-2xl border border-border bg-background p-6 md:p-7">
                <div>
                  <h3 className="font-display text-xl font-semibold text-foreground md:text-2xl">
                    {group.group}
                  </h3>
                  {group.note && (
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {group.note}
                    </p>
                  )}
                </div>
                {group.modules.length > 0 && (
                  <ul className="flex flex-col gap-2.5">
                    {group.modules.map((module) => (
                      <li
                        key={module}
                        className="flex items-start gap-3 text-sm leading-relaxed text-foreground"
                      >
                        <span
                          aria-hidden
                          className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-gold"
                        />
                        {module}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TiltCard>
          ))}
        </ScrollReveal>

        {caveat && (
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-8 max-w-3xl border-l-2 border-brand-gold/40 pl-4 text-xs leading-relaxed text-muted-foreground">
              {caveat}
            </p>
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}
